import React, { useState } from "react";
import api from "../../../../api/client";
import AssetModal, { Field } from "./AssetModal";
import { CRITICALITY, errorMessage, toInputDate } from "./assetUtils";

const COMMON_TYPES = [
  "CNC Lathe", "CNC Milling", "VMC", "HMC", "Lathe", "Drilling", "Grinding", "Press",
  "Injection Moulding", "Welding", "Compressor", "Furnace", "Conveyor", "Boiler", "Generator", "Other",
];

// Add or edit a machine. The photo is uploaded separately from the machine
// page, so this form stays a plain JSON save.
export default function MachineModal({ machine, people, onClose, onSaved }) {
  const editing = Boolean(machine);
  const [form, setForm] = useState(() => ({
    code: machine?.code || "",
    name: machine?.name || "",
    type: machine?.type || "",
    serialNumber: machine?.serialNumber || "",
    make: machine?.make || "",
    model: machine?.model || "",
    location: machine?.location || "",
    purchaseDate: toInputDate(machine?.purchaseDate),
    installDate: toInputDate(machine?.installDate),
    purchaseCost: machine?.purchaseCost ?? "",
    runningHours: machine?.runningHours ?? "",
    criticality: machine?.criticality || "medium",
    status: machine?.status === "down" ? "running" : machine?.status || "running",
    technicianId: machine?.technician?.id || "",
    notes: machine?.notes || "",
  }));
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { ...form };
      // A machine that is down stays down until its breakdown is resolved.
      if (machine?.status === "down") delete body.status;
      const res = editing
        ? await api.patch(`/manufacturing/assets/machines/${machine.id}`, body)
        : await api.post("/manufacturing/assets/machines", body);
      let saved = res.data.data;
      if (image) {
        const data = new FormData();
        data.append("file", image);
        saved = (await api.post(`/manufacturing/assets/machines/${saved.id}/image`, data)).data.data;
      }
      onSaved(saved);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save the machine."));
      setBusy(false);
    }
  };

  const technician = people.find((p) => p.id === form.technicianId);

  return (
    <AssetModal
      title={editing ? `Edit ${machine.name}` : "Add machine"}
      wide
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={editing ? "Save changes" : "Add machine"}
    >
      <div className="am-row-3">
        <Field label="Asset code *" hint="Short tag people say on chat, e.g. CNC-01">
          <input className="am-input" value={form.code} onChange={set("code")} required maxLength={40} />
        </Field>
        <Field label="Name *">
          <input className="am-input" value={form.name} onChange={set("name")} required maxLength={120} />
        </Field>
        <Field label="Type *">
          <input className="am-input" list="am-machine-types" value={form.type} onChange={set("type")} required maxLength={80} />
          <datalist id="am-machine-types">
            {COMMON_TYPES.map((t) => <option key={t} value={t} />)}
          </datalist>
        </Field>
      </div>
      <div className="am-row-3">
        <Field label="Serial number">
          <input className="am-input" value={form.serialNumber} onChange={set("serialNumber")} maxLength={120} />
        </Field>
        <Field label="Make">
          <input className="am-input" value={form.make} onChange={set("make")} placeholder="e.g. Haas, Ace, BFW" maxLength={80} />
        </Field>
        <Field label="Model">
          <input className="am-input" value={form.model} onChange={set("model")} maxLength={80} />
        </Field>
      </div>
      <div className="am-row-3">
        <Field label="Location">
          <input className="am-input" value={form.location} onChange={set("location")} placeholder="e.g. Shop floor, Bay 2" maxLength={120} />
        </Field>
        <Field label="Criticality">
          <select className="am-select" value={form.criticality} onChange={set("criticality")}>
            {CRITICALITY.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Status" hint={machine?.status === "down" ? "Down until its breakdown is resolved" : undefined}>
          <select className="am-select" value={form.status} onChange={set("status")} disabled={machine?.status === "down"}>
            <option value="running">Running</option>
            <option value="maintenance">Under maintenance</option>
            <option value="retired">Retired</option>
          </select>
        </Field>
      </div>
      <div className="am-row">
        <Field
          label="Maintenance technician"
          hint={
            technician && !technician.reachable
              ? "⚠️ This person has no WhatsApp or Telegram set, so breakdown alerts can't reach them."
              : "Gets this machine's breakdown alerts and PM reminders on chat."
          }
        >
          <select className="am-select" value={form.technicianId} onChange={set("technicianId")}>
            <option value="">— Use the fallback technicians —</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.reachable ? "" : " (no chat)"}</option>)}
          </select>
        </Field>
        <Field label="Running hours (meter)" hint="Drives hour-based preventive maintenance.">
          <input className="am-input" type="number" min="0" step="0.1" value={form.runningHours} onChange={set("runningHours")} />
        </Field>
      </div>
      <div className="am-row-3">
        <Field label="Purchase date">
          <input className="am-input" type="date" value={form.purchaseDate} onChange={set("purchaseDate")} />
        </Field>
        <Field label="Installation date" hint="Downtime figures start here">
          <input className="am-input" type="date" value={form.installDate} onChange={set("installDate")} />
        </Field>
        <Field label="Purchase cost (₹)">
          <input className="am-input" type="number" min="0" step="0.01" value={form.purchaseCost} onChange={set("purchaseCost")} />
        </Field>
      </div>
      {!editing && (
        <Field label="Photo" hint="JPG, PNG or WebP, up to 10 MB">
          <input className="am-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setImage(e.target.files[0] || null)} />
        </Field>
      )}
      <Field label="Notes">
        <textarea className="am-textarea" value={form.notes} onChange={set("notes")} maxLength={2000} />
      </Field>
    </AssetModal>
  );
}
